import { BasicButton, Loading, Typography } from "@/components";
import useBackward from "@/hooks/useBackward";
import { useBoolean } from "@/hooks/useBoolean";
import useEventMutation from "@/hooks/useEventMutation";
import type { CreateOrUpdateCalendarFieldSchema } from "@/schemas";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import {
	Controller,
	type SubmitHandler,
	useFormContext,
} from "react-hook-form";
import { toast } from "sonner";

const EventForm = () => {
	const navigate = useNavigate();
	const onBack = useBackward();
	const {
		reset,
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useFormContext<CreateOrUpdateCalendarFieldSchema>();

	const {
		value: openDatePicker,
		setTrue: onOpenDatePicker,
		setFalse: onCloseDatePicker,
	} = useBoolean();
	const {
		value: openStartTimePicker,
		setTrue: onOpenStartTimePicker,
		setFalse: onCloseStartTimePicker,
	} = useBoolean();
	const {
		value: openEndTimePicker,
		setTrue: onOpenEndTimePicker,
		setFalse: onCloseEndTimePicker,
	} = useBoolean();

	const { create, update } = useEventMutation();

	const onSubmit = useCallback<
		SubmitHandler<CreateOrUpdateCalendarFieldSchema>
	>(
		(data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			const payload = {
				title: data.title,
				description: data.description,
				start_datetime: data.date.format("YYYY-MM-DD HH:mm:ss Z"),
				end_datetime: (data.end ?? data.date).format("YYYY-MM-DD HH:mm:ss Z"),
				with_timezone: true,
			};
			if (data.id) {
				Object.assign(payload, { id: data.id });
				return update.mutateAsync(payload as any, {
					onSuccess: () => {
						reset();
						toast.success("Event updated");
						return navigate({
							replace: true,
							to: "/dashboard/calendar",
							search: {
								date: data.date.startOf("month").format("DD-MM-YYYY"),
							},
						});
					},
				});
			}
			return create.mutateAsync(payload, {
				onSuccess: () => {
					reset();
					toast.success("Event added");
					return onBack();
				},
			});
		},
		[update, create, reset, onBack, navigate],
	);

	useBlocker({
		blockerFn: () => {
			reset();
			return true;
		},
	});

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col">
			<div className="container flex-1 flex flex-col gap-3 pt-4">
				<Card
					variant="elevation"
					className="[&_.MuiCardContent-root]:!p-4 !rounded-xl"
				>
					<CardContent className="flex flex-col gap-8">
						<Typography fontWeight={900} fontSize={16}>
							Event Details
						</Typography>
						<Controller
							control={control}
							name="title"
							render={({ field, fieldState }) => (
								<TextField
									label="Event Title"
									placeholder="Enter event title"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									inputRef={field.ref}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error?.message}
									helperText={`${field.value?.length ?? 0}/50`}
									slotProps={{
										formHelperText: {
											className: "!text-right",
										},
										inputLabel: {
											shrink: true,
										},
									}}
								/>
							)}
						/>
						<Controller
							control={control}
							name="description"
							defaultValue=""
							render={({ field, fieldState }) => (
								<div className="relative w-full">
									<TextField
										multiline
										rows={3}
										label="Description (Optional)"
										placeholder="Enter event description"
										className="[&_.MuiInputBase-root]:!rounded-xl !w-full"
										inputRef={field.ref}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										error={!!fieldState.error?.message}
										slotProps={{
											inputLabel: {
												shrink: true,
											},
										}}
									/>
									<Typography
										fontWeight={400}
										fontSize={12}
										className="absolute right-4 bottom-2"
									>
										{field.value?.length ?? 0}/100
									</Typography>
								</div>
							)}
						/>
					</CardContent>
				</Card>
				<Card
					variant="elevation"
					className="[&_.MuiCardContent-root]:!p-4 !rounded-xl"
				>
					<CardContent className="flex flex-col gap-4">
						<Typography fontWeight={900} fontSize={16}>
							Event Time
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Controller
								control={control}
								name="date"
								render={({ field }) => (
									<>
										<MobileDatePicker
											open={openDatePicker}
											onClose={onCloseDatePicker}
											value={field.value}
											onAccept={field.onChange}
											slots={{
												field: () => null,
											}}
										/>
										<MobileTimePicker
											open={openStartTimePicker}
											onClose={onCloseStartTimePicker}
											value={field.value}
											onAccept={field.onChange}
											slots={{
												field: () => null,
											}}
										/>
										<Controller
											control={control}
											name="end"
											render={({ field: endField }) => (
												<>
													<MobileTimePicker
														open={openEndTimePicker}
														onClose={onCloseEndTimePicker}
														value={endField.value}
														onAccept={endField.onChange}
														minTime={field.value}
														slots={{
															field: () => null,
														}}
													/>
													<ul>
														<li>
															<button
																type="button"
																className="w-full flex py-2 gap-1 justify-center items-center"
																onClick={onOpenDatePicker}
															>
																<Typography
																	component="span"
																	fontWeight={700}
																	fontSize={16}
																	align="left"
																	className="flex-1"
																>
																	Date
																</Typography>
																<Typography
																	component="span"
																	fontWeight={400}
																	fontSize={16}
																>
																	{field.value.format("ddd, DD MMMM YYYY")}
																</Typography>
																<NavigateNextRoundedIcon />
															</button>
														</li>
														<li>
															<Divider />
														</li>
														<li>
															<button
																type="button"
																className="w-full flex py-2 gap-1 justify-center items-center"
																onClick={onOpenStartTimePicker}
															>
																<Typography
																	component="span"
																	fontWeight={700}
																	fontSize={16}
																	align="left"
																	className="flex-1"
																>
																	Start Time
																</Typography>
																<Typography
																	component="span"
																	fontWeight={400}
																	fontSize={16}
																>
																	{field.value.format("HH:mm A")}
																</Typography>
																<NavigateNextRoundedIcon />
															</button>
														</li>
														<li>
															<Divider />
														</li>
														<li>
															<button
																type="button"
																className="w-full flex py-2 gap-1 justify-center items-start"
																onClick={onOpenEndTimePicker}
															>
																<Typography
																	component="span"
																	fontWeight={700}
																	fontSize={16}
																	align="left"
																	className="flex-1"
																>
																	End Time
																</Typography>
																<Typography
																	component="span"
																	fontWeight={400}
																	fontSize={16}
																>
																	{endField.value?.format("HH:mm A")}
																</Typography>
																<NavigateNextRoundedIcon />
															</button>
														</li>
													</ul>
												</>
											)}
										/>
									</>
								)}
							/>
						</LocalizationProvider>
					</CardContent>
				</Card>
			</div>
			<div className="container bg-white py-4 shadow">
				<BasicButton
					type="submit"
					variant="contained"
					color="secondary"
					className="!h-12 text-sm font-bold !shadow-none"
					fullWidth
					disable={!isDirty || !isValid}
				>
					Save
				</BasicButton>
			</div>
			<Loading status={isSubmitting} />
		</form>
	);
};

export default EventForm;
